import type { OrderLine } from "~/@types/orderLines";
import type { ColDef } from "~/components/grid/AgGrid";

import { OrderEntryUnit, OrderStatus } from "~/gql/graphqlBase.generated";
import { aggregateLineUnitTotals, type LineUnitTotals } from "~/helpers/grid/orderLineUnitSummary";
import { calcTotalEaches, hasQuantityPairDrift } from "~/helpers/lineItemCalculations";
import formatLocaleNumber from "~/helpers/localeNumberFormat";
import shippingUomLabels from "~fulfill/product/helpers/shippingUomLabels";

export interface LineUnitsSource {
	handlingUom?: null | number;
	resolvedEachesPerUnit?: null | number;
}

export interface LinePriceSource {
	overridePrice?: null | number;
	price?: null | number;
}

export interface StoredEachesLine {
	orderedQuantity?: null | number;
	quantity?: null | number;
}

export interface AnchoredEachesLine extends StoredEachesLine {
	orderedEaches?: null | number;
}

export interface EntryQuantityLine extends AnchoredEachesLine, LineUnitsSource {
	eachesQuantity?: null | number;
	orderedUnits?: null | number;
}

export interface TotalEachesBasis {
	eachesQuantity: number;
	handlingUom: number;
}

export interface StageStampDrift {
	eaches: number;
	eachesPerUnit: number;
	stampedUnits: number;
	wholeUnits: number;
}

// wsapi names these opposite to their meaning: `quantity` counts EACHES and `eachesQuantity` counts UNITS.
export interface WireQuantities {
	eachesQuantity?: number;
	quantity: number;
}

export const UNIT_DECIMALS = 2;

export const MIN_STATED_PACK_SIZE = 1;

// AG Grid hands formatters the raw edit text on some refresh paths, so every input is coerced before use.
export const asNumber = (value: unknown): null | number => {
	if (value == null || value === "") return null;
	const parsed = typeof value === "number" ? value : Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

export const lineEachesPerUnit = (line: LineUnitsSource | null | undefined): number =>
	line?.resolvedEachesPerUnit || line?.handlingUom || 1;

export const isEachesEntry = (orderEntryUnit: OrderEntryUnit | null | undefined): boolean =>
	orderEntryUnit === OrderEntryUnit.Eaches;

export const eachesToUnits = (eaches: unknown, eachesPerUnit: number): null | number => {
	const count = asNumber(eaches);
	return count == null ? null : count / (eachesPerUnit > 0 ? eachesPerUnit : 1);
};

export const unitsToEaches = (units: unknown, eachesPerUnit: number): null | number => {
	const count = asNumber(units);
	return count == null ? null : count * (eachesPerUnit > 0 ? eachesPerUnit : 1);
};

export const unitsFromEaches = (eaches: unknown, eachesPerUnit: number): null | number => {
	const count = asNumber(eaches);
	if (count == null) return null;
	return Math.ceil(count / (eachesPerUnit > 0 ? eachesPerUnit : 1));
};

export const stageStampDrift = (
	stageEaches: unknown,
	stampedUnits: null | number | undefined,
	line: LineUnitsSource | null | undefined
): null | StageStampDrift => {
	const eaches = asNumber(stageEaches);
	if (eaches == null || stampedUnits == null) return null;
	const eachesPerUnit = lineEachesPerUnit(line);
	const wholeUnits = unitsFromEaches(eaches, eachesPerUnit) ?? eaches;
	return wholeUnits === stampedUnits ? null : { eaches, eachesPerUnit, stampedUnits, wholeUnits };
};

export const statedOrderedEaches = (line: AnchoredEachesLine | null | undefined): null | number =>
	line?.orderedEaches ?? line?.orderedQuantity ?? line?.quantity ?? null;

export const orderedEachesFor = (line: AnchoredEachesLine | null | undefined): number => statedOrderedEaches(line) ?? 0;

// `eachesQuantity` is the stage mirror wsapi moves on allocate/acknowledge/ship; only an unsaved typed row reads it.
export const orderedUnitsFor = (line: EntryQuantityLine | null | undefined, eachesEntry: boolean): null | number => {
	if (eachesEntry) {
		return line?.orderedUnits ?? unitsFromEaches(statedOrderedEaches(line), lineEachesPerUnit(line));
	}
	if (line?.orderedUnits != null) return line.orderedUnits;
	const orderedEaches = statedOrderedEaches(line);
	return orderedEaches == null
		? eachesToUnits(line?.eachesQuantity, 1)
		: eachesToUnits(orderedEaches, lineEachesPerUnit(line));
};

export interface BilledAcknowledgedLine extends EntryQuantityLine {
	acknowledgedEaches?: null | number;
	acknowledgedQuantity?: null | number;
	acknowledgedUnits?: null | number;
}

const statedBilledOrderedEaches = (line: BilledAcknowledgedLine | null | undefined): null | number => {
	if (line?.orderedEaches != null) return line.orderedEaches;
	const units = line?.orderedUnits ?? line?.eachesQuantity ?? line?.quantity;
	return units == null ? null : units * lineEachesPerUnit(line);
};

export const billedOrderedEaches = (line: BilledAcknowledgedLine | null | undefined): number =>
	statedBilledOrderedEaches(line) ?? 0;

// wsapi bills an unstamped acknowledged line on its ordered eaches; acknowledgedQuantity is only a mirror there.
export const billedAcknowledgedEaches = (
	line: BilledAcknowledgedLine | null | undefined,
	status: OrderStatus | undefined
): null | number => {
	if (line?.acknowledgedUnits != null && line.acknowledgedQuantity != null) return line.acknowledgedQuantity;
	if (line?.acknowledgedQuantity != null) return billedOrderedEaches(line);
	const passedGate = status === OrderStatus.Acknowledged || status === OrderStatus.Picked;
	return passedGate ? statedBilledOrderedEaches(line) : null;
};

// wsapi's `acknowledgedEaches` is the figure the bill uses; the client derivation above only covers a server that omits it.
export const acknowledgedEachesFor = (
	line: BilledAcknowledgedLine | null | undefined,
	status: OrderStatus | undefined
): null | number => line?.acknowledgedEaches ?? billedAcknowledgedEaches(line, status);

export const hasOrderedUnitsDrift = (line: EntryQuantityLine | null | undefined): boolean =>
	line?.orderedUnits != null && hasQuantityPairDrift(line.orderedUnits, lineEachesPerUnit(line), line.orderedQuantity);

export const entryQuantityField = (eachesEntry: boolean): "eachesQuantity" | "orderedEaches" =>
	eachesEntry ? "orderedEaches" : "eachesQuantity";

export const entryQuantityFor = (line: EntryQuantityLine | null | undefined, eachesEntry: boolean): number =>
	eachesEntry ? orderedEachesFor(line) : (line?.eachesQuantity ?? 0);

export const isOrderedRow = (line: EntryQuantityLine | null | undefined, eachesEntry: boolean): boolean =>
	entryQuantityFor(line, eachesEntry) > 0;

export const enteredUnitsFor = (line: EntryQuantityLine | null | undefined, eachesEntry: boolean): null | number =>
	eachesEntry ? orderedUnitsFor(line, true) : entryQuantityFor(line, false);

export const applyEntryQuantity = (
	typed: unknown,
	eachesPerUnit: number,
	eachesEntry: boolean
): Pick<OrderLine, "eachesQuantity" | "orderedEaches" | "orderedQuantity" | "orderedUnits" | "quantity"> => {
	const entered = asNumber(typed) ?? 0;
	if (!eachesEntry) {
		const eaches = entered * (eachesPerUnit > 0 ? eachesPerUnit : 1);
		return {
			eachesQuantity: entered,
			orderedEaches: null,
			orderedQuantity: eaches,
			orderedUnits: entered,
			quantity: eaches
		};
	}
	const units = unitsFromEaches(entered, eachesPerUnit) ?? 0;
	return {
		eachesQuantity: units,
		orderedEaches: entered,
		orderedQuantity: entered,
		orderedUnits: units,
		quantity: entered
	};
};

export const toWireQuantities = (line: EntryQuantityLine | null | undefined, eachesEntry: boolean): WireQuantities => {
	if (eachesEntry) return { quantity: orderedEachesFor(line) };
	const eachesQuantity = line?.eachesQuantity || 0;
	return { eachesQuantity, quantity: calcTotalEaches(eachesQuantity, line?.handlingUom) };
};

export const handlingUomWhenNoShippingUom = (
	shippingUomId: null | string | undefined,
	statedPackSize: null | number | undefined
): number | undefined =>
	shippingUomId || !statedPackSize || statedPackSize < MIN_STATED_PACK_SIZE ? undefined : statedPackSize;

export const totalEachesBasis = (line: EntryQuantityLine | null | undefined, eachesEntry: boolean): TotalEachesBasis =>
	eachesEntry
		? { eachesQuantity: orderedEachesFor(line), handlingUom: 1 }
		: { eachesQuantity: line?.eachesQuantity ?? 0, handlingUom: line?.handlingUom ?? 1 };

/** A typed unit-family edit landed on the row as whole eaches: 0.87 flats on a 15-tray → 13. */
export const wholeEachesFromUnits = (units: unknown, eachesPerUnit: number): null | number => {
	const eaches = unitsToEaches(units, eachesPerUnit);
	return eaches == null ? null : Math.round(eaches);
};

export const fractionalEachesRefusalKey = (field: string, value: number): null | string => {
	if (Number.isInteger(value)) return null;
	if (field === "acknowledgedQuantity" || field === "shippedQuantity") return "orders.snack.wholePlantsUseUnits";
	if (field === "orderedQuantity") return "orders.snack.wholePlantsOrderedEaches";
	return null;
};

export const formatLineQuantities = (eaches: number, units: null | number, label?: null | string): string => {
	if (units == null || !label) return formatLocaleNumber(eaches);
	return eaches > 0
		? shippingUomLabels.unitCountWithEaches(Number(units.toFixed(UNIT_DECIMALS)), eaches, label)
		: formatUnits(units, label);
};

// `|| 0` because Intl prints the -0 that -0.001 cuts to as "-0".
export const formatUnits = (units: unknown, label?: null | string): string => {
	const count = asNumber(units);
	if (count == null) return "";
	const formatted = formatLocaleNumber(Number(count.toFixed(UNIT_DECIMALS)) || 0);
	return label ? `${formatted} (${label})` : formatted;
};

export const formatEaches = (eaches: unknown): string => {
	const count = asNumber(eaches);
	return count == null ? "" : formatLocaleNumber(count || 0);
};

export const displayOnlyFormatter = (format: (value: unknown) => string) =>
	({
		useValueFormatterForExport: false,
		valueFormatter: (params: { value: unknown }) => format(params.value)
	}) satisfies Pick<ColDef, "useValueFormatterForExport" | "valueFormatter">;

export const formatUngroupedUnits = (units: unknown, label?: null | string): string => {
	const count = asNumber(units);
	if (count == null) return "";
	const figure = `${Number(count.toFixed(UNIT_DECIMALS))}`;
	return label ? `${figure} (${label})` : figure;
};

// Scales before dividing: 29 / 100 * 100 is 28.999999999999996 in floating point, which would truncate to 0.28.
export const truncateUnits = (eaches: number, eachesPerUnit: number): number => {
	const pack = eachesPerUnit > 0 ? eachesPerUnit : 1;
	const truncated = Math.trunc((eaches * 10 ** UNIT_DECIMALS) / pack) / 10 ** UNIT_DECIMALS;
	return truncated === 0 ? 0 : truncated;
};

export const formatTruncatedUnits = (eaches: unknown, eachesPerUnit: number): string => {
	const count = asNumber(eaches);
	return count == null ? "" : formatUnits(truncateUnits(count, eachesPerUnit));
};

export const pricePerUnit = (line: LinePriceSource | null | undefined, eachesPerUnit: number): null | number => {
	const perEach = line?.overridePrice ?? line?.price;
	return perEach == null ? null : perEach * (eachesPerUnit > 0 ? eachesPerUnit : 1);
};

export const orderLineEaches = (line: StoredEachesLine | null | undefined): number =>
	line?.orderedQuantity ?? line?.quantity ?? 0;

export const orderLineEachesTotals = (lines: readonly StoredEachesLine[]): LineUnitTotals =>
	aggregateLineUnitTotals(lines, orderLineEaches);

export const orderLineUnitsTotal = (lines: readonly EntryQuantityLine[], eachesEntry: boolean): null | number => {
	const stated = lines.filter((line) => orderedUnitsFor(line, eachesEntry) != null);
	if (stated.length === 0) return null;
	return stated.reduce((total, line) => total + (orderedUnitsFor(line, eachesEntry) ?? 0), 0);
};

export const allocatedEachesForLine = (
	allocations: ReadonlyArray<{ lineItemId: string; quantity: number }> | null | undefined,
	lineItemId: string
): number =>
	(allocations ?? [])
		.filter((allocation) => allocation.lineItemId === lineItemId)
		.reduce((total, allocation) => total + allocation.quantity, 0);

export const shouldConfirmShipSave = (status: OrderStatus | undefined, lineAllocatedEaches: number): boolean =>
	status === OrderStatus.Picked || (status === OrderStatus.Shipped && lineAllocatedEaches > 0);

const orderLineUnits = {
	acknowledgedEachesFor,
	allocatedEachesForLine,
	applyEntryQuantity,
	asNumber,
	billedAcknowledgedEaches,
	billedOrderedEaches,
	displayOnlyFormatter,
	eachesToUnits,
	enteredUnitsFor,
	entryQuantityField,
	entryQuantityFor,
	formatEaches,
	formatLineQuantities,
	formatTruncatedUnits,
	formatUngroupedUnits,
	formatUnits,
	handlingUomWhenNoShippingUom,
	hasOrderedUnitsDrift,
	isEachesEntry,
	isOrderedRow,
	lineEachesPerUnit,
	orderedEachesFor,
	orderedUnitsFor,
	orderLineEaches,
	orderLineEachesTotals,
	orderLineUnitsTotal,
	pricePerUnit,
	shouldConfirmShipSave,
	stageStampDrift,
	totalEachesBasis,
	toWireQuantities,
	truncateUnits,
	unitsFromEaches,
	unitsToEaches,
	wholeEachesFromUnits
};

export default orderLineUnits;
